import { useState, useMemo } from 'react';
import { Flex, Badge, Card } from '@scale/scaleui-radix';
import { AuditRubricCriterion } from '@scale/shared-types';
import { ChatResponseV2, ThreadedChatResponse } from '@scale/shared/types/chat';
import { TaskType } from '@scale/shared/defaults';
import { useErrorCategoriesToMaxScoreMap } from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import { ViewCriterionName } from './criterionComponents/CriterionName';
import { FillCriterionSteps } from './criterionComponents/CriterionSteps';
import { FillCriterionAnswerOptions } from './criterionComponents/CriterionAnswerOptions';
import { FillCriterionErrorCategories } from './criterionComponents/CriterionErrorCategories';
import { FillCriterionJustification } from './criterionComponents/CriterionJustification';

export const CriterionToFill = ({
  projectId,
  criterion,
  response,
}: {
  projectId: string;
  criterion: AuditRubricCriterion;
  response: {
    type: TaskType;
    response: ThreadedChatResponse | ChatResponseV2;
  };
}) => {
  const { data: errorCategories, isLoading: isLoadingErrorCategories } =
    useErrorCategoriesToMaxScoreMap(projectId);

  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
  const selectedOptionScore = useMemo(() => {
    return criterion.answerOptions.find(option => option.text === selectedOption)?.score;
  }, [criterion.answerOptions, selectedOption]);

  const [selectedErrorCategories, setSelectedErrorCategories] = useState<string[]>([]);

  const [feedback, setFeedback] = useState<string>('');

  const isComplete =
    selectedOptionScore !== undefined && (!criterion.requiresJustification || feedback.length > 0);
  return (
    <div style={{ minWidth: 200 }}>
      <Card>
        <Flex direction="column" gap="4" width="100%">
          <ViewCriterionName
            criterion={criterion}
            renderedActions={isComplete ? null : <Badge color="warning">Incomplete</Badge>}
            showQuestionText={false}
          />
          <FillCriterionSteps criterion={criterion} response={response} />
          <div className="h-1" />
          <FillCriterionAnswerOptions
            criterion={criterion}
            errorCategories={errorCategories ?? {}}
            isLoadingErrorCategories={isLoadingErrorCategories}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            selectedOptionScore={selectedOptionScore}
          />
          <FillCriterionErrorCategories
            errorCategories={criterion.errorCategories}
            selectedErrorCategories={selectedErrorCategories}
            setSelectedErrorCategories={setSelectedErrorCategories}
          />
          {criterion.requiresJustification && (
            <FillCriterionJustification feedback={feedback} setFeedback={setFeedback} />
          )}
        </Flex>
      </Card>
    </div>
  );
};
